import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import jsonData from "../../../../current-survey-numeric.json"; // Ensure the path is correct

const TopRightChart = ({ filterCriteria }) => {
  const [filteredScores, setFilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [unfilteredScores, setUnfilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  useEffect(() => {
    const processScores = (data) => {
      const scores = {
        NR: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
      };

      data.forEach((item) => {
        const score = item.MQ0007;
        if (
          score === 1 ||
          score === 2 ||
          score === 3 ||
          score === 4 ||
          score === 5
        ) {
          scores[score]++;
        } else {
          scores.NR++;
        }
      });

      return scores;
    };

    const filteredData = jsonData.filter((item) => {
      return Object.keys(filterCriteria).every(
        (key) =>
          filterCriteria[key].length === 0 ||
          filterCriteria[key].includes(item[key])
      );
    });

    // Calculate filtered and unfiltered scores
    const newFilteredScores = processScores(filteredData);
    const newUnfilteredScores = processScores(jsonData);

    // Debugging: Log the filtered and unfiltered scores
    // console.log("Filtered Scores:", newFilteredScores);
    // console.log("Unfiltered Scores:", newUnfilteredScores);

    setFilteredScores(newFilteredScores);
    setUnfilteredScores(newUnfilteredScores);
  }, [filterCriteria]);

  const labelToScoreKeyMap = {
    "Very Low": 1,
    "Below Average": 2,
    "Average": 3,
    "Above Average": 4,
    "Very High": 5,
    "NR": "NR", // Assuming NR is a valid key in filteredScores
  };

  const seriesData = Object.keys(filteredScores).map((key) => {
    return {
      x: key,
      y: filteredScores[key],
      goals: [
        {
          name: "Unfiltered",
          value: unfilteredScores[key],
          strokeHeight: 12,
          strokeColor: "#6D6D6D",
        },
      ],
    };
  });

  const options = {
    chart: {
      type: "bar",
      height: "300px",
    },
    colors: ["#F28E2B", "#F28E2B", "#F1CE63", "#499894", "#499894", "#BAB0AC"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        horizontal: true,
        distributed: true,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opt) {
        const totalFiltered = Object.values(filteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        );
        const xAxisLabel = opt.w.globals.labels[opt.dataPointIndex];
        const scoreKey = labelToScoreKeyMap[xAxisLabel];
        const filteredValue = filteredScores[scoreKey] || 0;

        const totalUnfiltered = Object.values(unfilteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        );
        const unfilteredValue =
          opt.w.config.series[opt.seriesIndex].data[opt.dataPointIndex].goals[0]
            .value || 0;

        const filteredPercentage =
          totalFiltered > 0
            ? ((filteredValue / totalFiltered) * 100).toFixed(1)
            : "0.0";
        const unfilteredPercentage =
          totalUnfiltered > 0
            ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(1)
            : "0.0";

        return `${filteredPercentage}% | ${unfilteredPercentage}%`; // Display filtered and unfiltered percentages
      },
      style: {
        colors: ["#000"], // Set data labels to black
      },
      offsetX: 65, // Move labels off the bars
    },
    xaxis: {
      categories: [
        "Very Low",
        "Below Average",
        "Average",
        "Above Average",
        "Very High",
        "NR",
      ],
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const xAxisLabel = w.globals.labels[dataPointIndex];
        const scoreKey = labelToScoreKeyMap[xAxisLabel];
        const totalFiltered = Object.values(filteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        );
        const filteredValue = filteredScores[scoreKey] || 0;

        const totalUnfiltered = Object.values(unfilteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        );
        const unfilteredValue =
          w.config.series[seriesIndex].data[dataPointIndex].goals[0].value || 0;

        const filteredPercentage =
          totalFiltered > 0
            ? ((filteredValue / totalFiltered) * 100).toFixed(1)
            : "0.0";
        const unfilteredPercentage =
          totalUnfiltered > 0
            ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(1)
            : "0.0";

        return `<div class="apexcharts-tooltip-text">
                  <div id="tooltip-title">Response:&nbsp; ${xAxisLabel}</div>  
                  <ul>
                  <li>Filtered: &nbsp; ${filteredPercentage}%</li>
                  <li>Unfiltered: &nbsp; ${unfilteredPercentage}%</li>
                  </ul>
                </div>`;
      },
    },
    annotations: {
      points: Object.keys(unfilteredScores).map((key, index) => {
        return {
          x: key,
          y: unfilteredScores[key],
          marker: {
            size: 5, // Make the marker thicker
            fillColor: "#fff",
            strokeColor: "#fff",
          },
          label: {
            borderColor: "#fff",
            offsetY: 0,
            style: {
              color: "#fff",
              background: "#fff",
            },
            text: `${unfilteredScores[key]}`,
          },
        };
      }),
    },
    legend: {
      show: false,
      labels: {
        useSeriesColors: true,
      },
    },
  };

  return (
    <div id="d08-top-right">
      <Chart
        options={options}
        series={[{ name: "Filtered", data: seriesData }]} // Set the name for the filtered data
        type="bar"
        height="300px"
      />
    </div>
  );
};

export default TopRightChart;

//  Note:  This chart was moved to the Lower Left side of the page to accomodate the 'Questions' text box
